import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import ListContainer from '../components/ListContainer';
import API_BASE_URL from '../config/api';
import CooperativeMachinePerformanceReportAR from './CooperativeMachinePerformanceReportAR';

const CooperativeMachinePerformanceReportDR = ({ user, mode = 'dr' }) => {
  // Filter States
  const [periods, setPeriods] = useState([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState(null);

  // Drilldown level state:
  // isRCS mode:
  // selectedDivision: null = Level 0 (Division View), string = Level 1 (District View for that division)
  // selectedDistrict: null = Level 1 (or 0), string = Level 2 (AR Circles under that district)
  // selectedArCircle: null = Level 0, 1 or 2, string = Level 3 (Society performance under that AR circle)
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedArCircle, setSelectedArCircle] = useState(null);

  // Report Data States
  const [loading, setLoading] = useState(true);
  const [machines, setMachines] = useState([]);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});

  // 1. Fetch Periods
  useEffect(() => {
    const fetchPeriods = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/periods`);
        const data = await res.json();
        if (data.success && Array.isArray(data.periods) && data.periods.length > 0) {
          setPeriods(data.periods);
          const latest = data.periods[0];
          setSelectedPeriodId(latest._id);
          setSelectedPeriod(latest);
        }
      } catch (err) {
        console.error('Error fetching periods:', err);
      }
    };
    fetchPeriods();
  }, []);

  // 2. Fetch DR / RCS Performance Report Data
  const fetchReportData = useCallback(async (perId = selectedPeriodId, divName = selectedDivision, distName = selectedDistrict, arName = selectedArCircle) => {
    setLoading(true);
    try {
      const userEmail = user?.email || user?.EmailID || '';
      const queryParams = new URLSearchParams({
        periodId: perId
      });

      if (mode === 'rcs') {
        queryParams.append('rcsEmail', userEmail);
      } else {
        queryParams.append('drEmail', userEmail);
      }

      if (divName) {
        queryParams.append('divisionName', divName);
      }
      if (distName) {
        queryParams.append('districtName', distName);
      }
      if (arName) {
        queryParams.append('arName', arName);
      }

      const res = await fetch(`${API_BASE_URL}/api/reports/dr-performance?${queryParams.toString()}`);
      const data = await res.json();

      if (data.success) {
        setMachines(data.machines || []);
        setRows(data.rows || []);
        setTotals(data.totals || {});
      }
    } catch (err) {
      console.error('Error fetching performance report:', err);
    } finally {
      setLoading(false);
    }
  }, [user, mode, selectedPeriodId, selectedDivision, selectedDistrict, selectedArCircle]);

  useEffect(() => {
    if (selectedPeriodId) {
      fetchReportData(selectedPeriodId, selectedDivision, selectedDistrict, selectedArCircle);
    }
  }, [selectedPeriodId, selectedDivision, selectedDistrict, selectedArCircle, fetchReportData]);

  // Handle Period Change
  const handlePeriodChange = (e) => {
    const periodId = e.target.value;
    setSelectedPeriodId(periodId);
    const matched = periods.find(p => p._id === periodId);
    if (matched) {
      setSelectedPeriod(matched);
    }
  };

  // Handle Row Click
  const handleDivisionClick = (divName) => {
    setSelectedDivision(divName);
    setSelectedDistrict(null);
    setSelectedArCircle(null);
  };

  const handleDistrictClick = (distName) => {
    setSelectedDistrict(distName);
    setSelectedArCircle(null);
  };

  const handleArClick = (arName) => {
    setSelectedArCircle(arName);
  };

  // Handle Back Buttons
  const handleBackToDivision = () => {
    setSelectedDivision(null);
    setSelectedDistrict(null);
    setSelectedArCircle(null);
  };

  const handleBackToDistrict = () => {
    setSelectedDistrict(null);
    setSelectedArCircle(null);
  };

  const handleBackToAr = () => {
    setSelectedArCircle(null);
  };

  // Export Transactions Handler
  const [exportingTx, setExportingTx] = useState(false);

  const handleExportTransactions = async () => {
    try {
      setExportingTx(true);
      const userEmail = user?.email || user?.EmailID || '';
      const queryParams = new URLSearchParams({
        periodId: selectedPeriodId
      });
      if (mode === 'rcs') {
        queryParams.append('rcsEmail', userEmail);
      } else {
        queryParams.append('drEmail', userEmail);
      }
      if (selectedDivision) {
        queryParams.append('divisionName', selectedDivision);
      }
      if (selectedDistrict) {
        queryParams.append('districtName', selectedDistrict);
      }
      if (selectedArCircle) {
        queryParams.append('arName', selectedArCircle);
      }

      const res = await fetch(`${API_BASE_URL}/api/transactions/export-dr?${queryParams.toString()}`);
      const data = await res.json();

      if (data.success && Array.isArray(data.rows)) {
        const headers = [
          "Division",
          "District",
          "AR Circle",
          "Village",
          "Society",
          "FarmerMobileNo",
          "FarmerName",
          "FatherName",
          "FarmerProfile",
          "MachineCode",
          "Machine",
          "MachineHrs",
          "ChargesPerHrs",
          "MachineRental",
          "TractorRequired",
          "TractorRental",
          "TotalRental",
          "Transaction Date"
        ];

        const csvRows = [headers.join(',')];

        data.rows.forEach(r => {
          csvRows.push([
            `"${r.Division || ''}"`,
            `"${r.District || ''}"`,
            `"${r.ARCircle || ''}"`,
            `"${r.Village || ''}"`,
            `"${r.Society || ''}"`,
            `"${r.FarmerMobileNo || ''}"`,
            `"${r.FarmerName || ''}"`,
            `"${r.FatherName || ''}"`,
            `"${r.FarmerProfile || ''}"`,
            `"${r.MachineCode || ''}"`,
            `"${r.Machine || ''}"`,
            `"${r.MachineHrs !== undefined ? r.MachineHrs : ''}"`,
            `"${r.ChargesPerHrs !== undefined ? r.ChargesPerHrs : ''}"`,
            `"${r.MachineRental !== undefined ? r.MachineRental : ''}"`,
            `"${r.TractorRequired || 'false'}"`,
            `"${r.TractorRental !== undefined ? r.TractorRental : ''}"`,
            `"${r.TotalRental !== undefined ? r.TotalRental : ''}"`,
            `"${r.TransactionDate || ''}"`
          ].join(','));
        });

        const csvContent = "data:text/csv;charset=utf-8," + csvRows.join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        const distName = selectedDistrict || selectedDivision || 'All';
        link.setAttribute("download", `Transactions_${distName}_${selectedPeriod?.PERIOD || 'Period'}_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Error exporting transactions:', err);
    } finally {
      setExportingTx(false);
    }
  };

  // Export Society-Wise Machine Summary Handler
  const [exportingSocSummary, setExportingSocSummary] = useState(false);

  const handleExportSocietySummary = async () => {
    try {
      setExportingSocSummary(true);
      const userEmail = user?.email || user?.EmailID || '';
      const queryParams = new URLSearchParams({
        periodId: selectedPeriodId
      });
      if (mode === 'rcs') {
        queryParams.append('rcsEmail', userEmail);
      } else {
        queryParams.append('drEmail', userEmail);
      }
      if (selectedDivision) {
        queryParams.append('divisionName', selectedDivision);
      }
      if (selectedDistrict) {
        queryParams.append('districtName', selectedDistrict);
      }
      if (selectedArCircle) {
        queryParams.append('arName', selectedArCircle);
      }

      const res = await fetch(`${API_BASE_URL}/api/reports/society-machine-summary?${queryParams.toString()}`);
      const data = await res.json();

      if (data.success && Array.isArray(data.rows) && Array.isArray(data.machines)) {
        const headers = ["DIVISION", "DISTRICT", "AR CIRCLE", "SOCIETY"];
        data.machines.forEach(m => {
          headers.push(
            `${m.name} COUNT`,
            `${m.name} AUTHORISED COUNT`,
            `${m.name} CHARGEABLE HRS`,
            `${m.name} FREE HRS`,
            `${m.name} TOTAL HRS`,
            `${m.name} AVG`,
            `${m.name} UTILIZATION`
          );
        });

        const csvRows = [headers.join(',')];

        data.rows.forEach(r => {
          const line = [
            `"${r.DIVISION || ''}"`,
            `"${r.DISTRICT || ''}"`,
            `"${r.AR_CIRCLE || ''}"`,
            `"${r.SOCIETY || ''}"`
          ];

          data.machines.forEach(m => {
            line.push(
              `"${r[`${m.code}_COUNT`] || 0}"`,
              `"${r[`${m.code}_AUTHORISED_COUNT`] || 0}"`,
              `"${r[`${m.code}_CHARGEABLE_HRS`] || '0.00'}"`,
              `"${r[`${m.code}_FREE_HRS`] || '0.00'}"`,
              `"${r[`${m.code}_TOTAL_HRS`] || '0.00'}"`,
              `"${r[`${m.code}_AVG`] || '0.00'}"`,
              `"${r[`${m.code}_UTILIZATION`] || '0 Utilization'}"`
            );
          });

          csvRows.push(line.join(','));
        });

        const csvContent = "data:text/csv;charset=utf-8," + csvRows.join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        const distName = selectedDistrict || 'All_Districts';
        link.setAttribute("download", `Society_Wise_Machine_Summary_${distName}_${selectedPeriod?.PERIOD || 'Period'}_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Error exporting society-wise machine summary:', err);
    } finally {
      setExportingSocSummary(false);
    }
  };

  // Export Formatted Excel Handler matching user specification
  const handleExportExcel = () => {
    if (!rows || rows.length === 0) return;

    // Row 1: Top Headers
    const row1 = [currentColumnLabel, "Acres covered", "No. of Members"];
    machines.forEach(m => {
      row1.push(m.name, "", "", "", "", "");
    });

    // Row 2: Sub-Metric Headers
    const row2 = ["", "", ""];
    machines.forEach(() => {
      row2.push(
        "No. of Machines",
        "Authorized Count",
        "Prev Avg. Utilization",
        "Curr Avg. Utilization",
        "Chargeable Hrs",
        "Free Hrs"
      );
    });

    const dataRows = [row1, row2];

    // Data rows
    rows.forEach(r => {
      const line = [r.name || '', r.acres || 0, r.noOfMembers || 0];
      machines.forEach(m => {
        const item = r[m.code] || {};
        line.push(
          item.noOfMachines || 0,
          item.authNoOfMachines || 0,
          Number((item.prevAvgUtilization || 0).toFixed(2)),
          Number((item.avgUtilization || 0).toFixed(2)),
          item.chargeableHrs || 0,
          item.freeHrs || 0
        );
      });
      dataRows.push(line);
    });

    // Totals row
    const totalsLine = ["TOTALS", totals.acres || 0, totals.noOfMembers || 0];
    machines.forEach(m => {
      const item = totals[m.code] || {};
      totalsLine.push(
        item.noOfMachines || 0,
        item.authNoOfMachines || 0,
        Number((item.prevAvgUtilization || 0).toFixed(2)),
        Number((item.avgUtilization || 0).toFixed(2)),
        item.chargeableHrs || 0,
        item.freeHrs || 0
      );
    });
    dataRows.push(totalsLine);

    // Create Worksheet & Workbook
    const ws = XLSX.utils.aoa_to_sheet(dataRows);

    // Merges
    const merges = [
      { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } },
      { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } },
      { s: { r: 0, c: 2 }, e: { r: 1, c: 2 } }
    ];

    machines.forEach((m, idx) => {
      const startCol = 3 + (idx * 6);
      const endCol = startCol + 5;
      merges.push({ s: { r: 0, c: startCol }, e: { r: 0, c: endCol } });
    });

    ws['!merges'] = merges;

    // Create workbook and write file
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Performance Report");

    const periodLabel = selectedPeriod?.seasonName || selectedPeriod?.PERIOD || 'Period';
    const viewName = selectedArCircle || selectedDistrict || selectedDivision || 'All';
    XLSX.writeFile(wb, `Cooperative_Machine_Performance_${viewName}_${periodLabel}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // MACHINE_CODES array for Zero Transactions CSV Export matching Power user society list export format
  const MACHINE_CODES = [
    "SUPERSEEDER", "ROTAVATOR", "MULCHER", "RMBPLOUGH", "ZEROTILLDRILL", "ROTARYSLASHER", 
    "PADDYSTRAWCHOPPER", "BALER", "RAKER", "REAPER", "TRACTOR", "HAPPYSEEDER", "SURFACESEEDER",
    "SMARTSEEDER", "SUPERSMS", "SHRUBMASTER", "CUTTERCUMSPREADER", "TRACTORTROLLEY", "LASERLEVELLER",
    "DIRECTSEEDERRICE", "MAIZEPLANTERMECHANICAL", "MAIZEPLANTERVACCUM", "SPRAYPUMP", "PRESSUREDISKS",
    "DISKLOCK", "BIOFERTILIZERSPREADER", "CULTIVATOR", "SUBSOILER", "POWERCLEANER", "DISCRAZOR",
    "HARROWTRIPPLE", "COTTONSEEDDRILL", "WATERTANK", "SUHAGA", "WHEATSEEDFAN", "SEEDGRINDINGMACHINE",
    "JINDRA", "COMBINE", "SEELARCOTTON", "GENERATOR", "WHEATSEEDDRILL", "PLOUGH", "GROUNDLEVELLER",
    "STRAWREAPER", "POTATOPLANTER", "TAWIAN", "SIMPLELEVELLER", "LOADER", "POTATODRILL",
    "MULTIDRILLMACHINE", "PUDDLER", "CHOPPER", "CORNDRILL", "PEASMACHINE", "SEEDDRILL", "CHIZLER",
    "PADDYPLANTER", "DIESELTANK", "BHAUNI", "TOTA", "TILLER", "KARAH", "TARFALI", "RAZOR",
    "THRESHER", "KARCHAMACHINE", "RATOR", "SUGARCANERAGER", "DISCHARROW", "CHAINKUPPI",
    "SUGARCANESEEDER", "GRINDER", "CHANGER", "TRIPPLEPLOUGH", "CORNHARVESTER", "CUTTER",
    "MOTORMACHINE", "DISCPLOUGH", "GAINIPLOUGH", "DUGPLOUGH", "OFFSETHARROW", "JAAL",
    "TARAKADI", "CARROTMACHINE", "DISCBEDMAKER", "LOADERBUCKET", "SEEDGRADER", "ACHATHALL",
    "TAWAWATTA", "JACK"
  ];

  // Export Societies with Zero Transactions Handler
  const [exportingZeroTxSoc, setExportingZeroTxSoc] = useState(false);

  const handleExportZeroTransactionsSocieties = async () => {
    try {
      setExportingZeroTxSoc(true);
      const res = await fetch(`${API_BASE_URL}/api/reports/zero-transactions-societies?periodId=${selectedPeriodId}`);
      const data = await res.json();

      if (data.success && Array.isArray(data.societies)) {
        const headers = [
          "Society Name", "Division", "District", "AR Circle", "Village", 
          "DR Name", "DR Mobile", "DR Email", 
          "AR Name", "AR Mobile", "AR Email", 
          "Inspector Name", "Inspector Mobile", "Inspector Email", 
          "Secretary Name", "Secretary Mobile", "Secretary Email",
          "Total Machines", "Total Authorized Machines"
        ];
        MACHINE_CODES.forEach(code => {
          headers.push(`${code} Count`);
          headers.push(`${code} Authorized Count`);
        });

        const csvRows = [headers.join(',')];

        data.societies.forEach(s => {
          const line = [
            `"${s.SOCIETY || s.SocietyName || ''}"`,
            `"${s.DIVISION || ''}"`,
            `"${s.DISTRICT || ''}"`,
            `"${s.AR || s.ARNAME || ''}"`,
            `"${s.VILLAGE || ''}"`,
            `"${s.DRNAME || ''}"`,
            `"${s.DRMOBILE || ''}"`,
            `"${s.DREMAILID || ''}"`,
            `"${s.ARNAME || ''}"`,
            `"${s.ARMOBILE || ''}"`,
            `"${s.AREMAILID || ''}"`,
            `"${s.INSPECTOR || ''}"`,
            `"${s.INSPECTORMOBILE || ''}"`,
            `"${s.INSPECTOREMAILID || ''}"`,
            `"${s.SECRETARY || ''}"`,
            `"${s.SECRETARYMOBILE || ''}"`,
            `"${s.SECRETARYEMAILID || ''}"`
          ];

          const mCounts = s.machineCounts || {};
          const aCounts = s.authMachineCounts || {};

          const totalM = mCounts.TOTALMACHINES !== undefined ? mCounts.TOTALMACHINES : Object.keys(mCounts).reduce((sum, key) => (typeof mCounts[key] === 'number' && key !== '__v' ? sum + mCounts[key] : sum), 0);
          const totalA = Object.keys(aCounts).reduce((sum, key) => (typeof aCounts[key] === 'number' && key !== '__v' ? sum + aCounts[key] : sum), 0);

          line.push(totalM, totalA);

          MACHINE_CODES.forEach(code => {
            line.push(mCounts[code] || 0);
            line.push(aCounts[code] || 0);
          });

          csvRows.push(line.join(','));
        });

        const csvContent = "data:text/csv;charset=utf-8," + csvRows.join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        const periodName = selectedPeriod?.seasonName || selectedPeriod?.PERIOD || 'Period';
        link.setAttribute("download", `Societies_Zero_Transactions_${periodName}_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Error exporting zero transactions societies:', err);
    } finally {
      setExportingZeroTxSoc(false);
    }
  };

  // Filter Bar UI
  const filterBar = (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', alignItems: 'center' }}>
        {/* Back Buttons */}
        {mode === 'rcs' ? (
          <>
            {selectedDivision && (
              <button
                type="button"
                onClick={handleBackToDivision}
                style={{
                  background: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500'
                }}
              >
                ⬅️ Back to Divisions
              </button>
            )}
            {selectedDistrict && (
              <button
                type="button"
                onClick={handleBackToDistrict}
                style={{
                  background: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500'
                }}
              >
                ⬅️ Back to Districts ({selectedDivision})
              </button>
            )}
            {selectedArCircle && (
              <button
                type="button"
                onClick={handleBackToAr}
                style={{
                  background: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500'
                }}
              >
                ⬅️ Back to AR Circles ({selectedDistrict})
              </button>
            )}
          </>
        ) : (
          <>
            {selectedArCircle ? (
              <button
                type="button"
                onClick={handleBackToAr}
                style={{
                  background: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500'
                }}
              >
                ⬅️ Back to AR Circles
              </button>
            ) : null}
          </>
        )}

        {/* Period Selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Period:</label>
          <select
            value={selectedPeriodId}
            onChange={handlePeriodChange}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              fontSize: '13px',
              backgroundColor: '#ffffff'
            }}
          >
            {periods.map(p => {
              const label = `${p.StartDate} to ${p.EndDate} (${p.seasonName || p.PERIOD})`;
              return <option key={p._id} value={p._id}>{label}</option>;
            })}
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
        {/* Export Formatted Excel Button */}
        <button
          type="button"
          onClick={handleExportExcel}
          style={{
            background: '#16a34a',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            padding: '8px 16px',
            fontWeight: '600',
            fontSize: '13px',
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          📗 Export Report (Excel)
        </button>

        {/* Export Transactions Button */}
        <button
          type="button"
          onClick={handleExportTransactions}
          disabled={exportingTx}
          style={{
            background: exportingTx ? '#94a3b8' : '#10b981',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            padding: '8px 16px',
            fontWeight: '600',
            fontSize: '13px',
            cursor: exportingTx ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          {exportingTx ? '⏳ Exporting...' : '📥 Export Transactions'}
        </button>

        {/* Export Society-Wise Machine Summary Button */}
        <button
          type="button"
          onClick={handleExportSocietySummary}
          disabled={exportingSocSummary}
          style={{
            background: exportingSocSummary ? '#94a3b8' : '#3b82f6',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            padding: '8px 16px',
            fontWeight: '600',
            fontSize: '13px',
            cursor: exportingSocSummary ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          {exportingSocSummary ? '⏳ Exporting...' : '📊 Export Society-Wise Machine Summary'}
        </button>
      </div>
    </div>
  );

  // Machine Headers background colors alternating for visual distinction matching reference UI
  const machineBgColors = [
    '#91d542', // Green
    '#facc15', // Yellow
    '#91d542',
    '#facc15',
    '#91d542',
    '#facc15',
    '#91d542',
    '#facc15',
    '#91d542',
    '#facc15',
    '#91d542',
    '#facc15',
    '#91d542'
  ];

  if (selectedArCircle) {
    return (
      <CooperativeMachinePerformanceReportAR
        user={user}
        arName={selectedArCircle}
        districtName={selectedDistrict}
        divisionName={selectedDivision}
        onBack={() => setSelectedArCircle(null)}
      />
    );
  }

  const titleHierarchy = [];
  if (selectedDivision) titleHierarchy.push(selectedDivision);
  if (selectedDistrict) titleHierarchy.push(selectedDistrict);

  const pageTitle = titleHierarchy.length > 0
    ? `Cooperative Machine Performance Report - ${titleHierarchy.join(" > ")}`
    : `Cooperative Machine Performance Report`;

  const currentColumnLabel = selectedArCircle
    ? 'SOCIETY'
    : selectedDistrict
    ? 'AR CIRCLE'
    : selectedDivision
    ? 'DISTRICT'
    : (mode === 'rcs' ? 'DIVISION' : 'DISTRICT');

  return (
    <ListContainer title={pageTitle} filterBar={filterBar}>
      {loading ? (
        <div className="loading-spinner-container">
          <div className="spinner"></div>
          <div>Loading Performance Report...</div>
        </div>
      ) : rows.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b', fontWeight: '500' }}>
          No data available for the selected period.
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto', borderRadius: '4px', border: '1px solid #cbd5e1' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'center' }}>
              <thead>
                {/* Row 1: Top Main Machine Headers */}
                <tr>
                  <th
                    rowSpan={2}
                    style={{
                      background: '#facc15',
                      color: '#1e293b',
                      padding: '10px 14px',
                      border: '1px solid #94a3b8',
                      fontWeight: '700',
                      fontSize: '13px',
                      minWidth: '160px',
                      verticalAlign: 'middle',
                      textAlign: 'left'
                    }}
                  >
                    {currentColumnLabel}
                  </th>
                  <th
                    rowSpan={2}
                    style={{
                      background: '#1e293b',
                      color: '#ffffff',
                      padding: '10px 10px',
                      border: '1px solid #94a3b8',
                      fontWeight: '700',
                      fontSize: '12px',
                      minWidth: '90px',
                      verticalAlign: 'middle'
                    }}
                  >
                    Acres covered
                  </th>
                  <th
                    rowSpan={2}
                    style={{
                      background: '#1e293b',
                      color: '#ffffff',
                      padding: '10px 10px',
                      border: '1px solid #94a3b8',
                      fontWeight: '700',
                      fontSize: '12px',
                      minWidth: '110px',
                      verticalAlign: 'middle'
                    }}
                  >
                    No. of Members
                  </th>

                  {machines.map((m, idx) => (
                    <th
                      key={m.code}
                      colSpan={6}
                      style={{
                        background: machineBgColors[idx % machineBgColors.length],
                        color: '#1e293b',
                        padding: '8px 10px',
                        border: '1px solid #94a3b8',
                        fontWeight: '700',
                        fontSize: '12px',
                        letterSpacing: '0.5px'
                      }}
                    >
                      {m.name}
                    </th>
                  ))}
                </tr>

                {/* Row 2: Sub-Metric Column Headers */}
                <tr style={{ background: '#334155', color: '#ffffff' }}>
                  {machines.map((m) => (
                    <React.Fragment key={`${m.code}-sub`}>
                      <th style={{ padding: '8px 6px', border: '1px solid #64748b', minWidth: '75px', fontWeight: '600' }}>
                        No. of Machines
                      </th>
                      <th style={{ padding: '8px 6px', border: '1px solid #64748b', minWidth: '85px', fontWeight: '600' }}>
                        Authorized Count
                      </th>
                      <th style={{ padding: '8px 6px', border: '1px solid #64748b', minWidth: '95px', fontWeight: '600', background: 'rgb(127, 127, 127)', color: '#ffffff' }}>
                        Prev Avg. Utilization
                      </th>
                      <th style={{ padding: '8px 6px', border: '1px solid #64748b', minWidth: '85px', fontWeight: '600', background: 'rgb(127, 127, 127)', color: '#ffffff' }}>
                        Curr Avg. Utilization
                      </th>
                      <th style={{ padding: '8px 6px', border: '1px solid #64748b', minWidth: '85px', fontWeight: '600' }}>
                        Chargeable Hrs
                      </th>
                      <th style={{ padding: '8px 6px', border: '1px solid #64748b', minWidth: '85px', fontWeight: '600' }}>
                        Free Hrs
                      </th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.map((row, rIdx) => (
                  <tr
                    key={row.name}
                    style={{
                      background: rIdx % 2 === 0 ? '#ffffff' : '#f8fafc',
                      borderBottom: '1px solid #e2e8f0'
                    }}
                  >
                    {/* Division / District / AR Circle / Society Column */}
                    <td
                      style={{
                        padding: '10px 14px',
                        border: '1px solid #cbd5e1',
                        fontWeight: '700',
                        textAlign: 'left',
                        color: selectedArCircle ? '#1e293b' : '#2563eb',
                        cursor: selectedArCircle ? 'default' : 'pointer',
                        textDecoration: selectedArCircle ? 'none' : 'underline'
                      }}
                      onClick={() => {
                        if (mode === 'rcs' && !selectedDivision) {
                          handleDivisionClick(row.name);
                        } else if (!selectedDistrict) {
                          handleDistrictClick(row.name);
                        } else if (!selectedArCircle) {
                          handleArClick(row.name);
                        }
                      }}
                      title={
                        selectedArCircle
                          ? row.name
                          : selectedDistrict
                          ? `Click to view societies under ${row.name}`
                          : selectedDivision
                          ? `Click to view AR circles under ${row.name}`
                          : (mode === 'rcs' ? `Click to view districts under ${row.name}` : `Click to view AR circles under ${row.name}`)
                      }
                    >
                      {row.name}
                    </td>

                    {/* Acres & No. of Members Columns */}
                    <td style={{ padding: '8px 6px', border: '1px solid #cbd5e1', fontWeight: '600', color: '#1e293b' }}>
                      {row.acres || 0}
                    </td>
                    <td style={{ padding: '8px 6px', border: '1px solid #cbd5e1', fontWeight: '600', color: '#1e293b' }}>
                      {row.noOfMembers || 0}
                    </td>

                    {/* Machine Columns */}
                    {machines.map((m) => {
                      const item = row[m.code] || {};
                      return (
                        <React.Fragment key={`${row.name}-${m.code}`}>
                          <td style={{ padding: '8px 6px', border: '1px solid #cbd5e1', color: '#1e293b' }}>
                            {item.noOfMachines || 0}
                          </td>
                          <td style={{ padding: '8px 6px', border: '1px solid #cbd5e1', color: '#1e293b', background: '#fef3c7' }}>
                            {item.authNoOfMachines || 0}
                          </td>
                          <td style={{ padding: '8px 6px', border: '1px solid #cbd5e1', background: 'rgb(127, 127, 127)', color: '#ffffff' }}>
                            {item.prevAvgUtilization ? item.prevAvgUtilization.toFixed(2) : '0.00'}
                          </td>
                          <td style={{ padding: '8px 6px', border: '1px solid #cbd5e1', background: 'rgb(127, 127, 127)', color: '#ffffff' }}>
                            {item.avgUtilization ? item.avgUtilization.toFixed(2) : '0.00'}
                          </td>
                          <td style={{ padding: '8px 6px', border: '1px solid #cbd5e1', color: '#1e293b' }}>
                            {item.chargeableHrs || 0}
                          </td>
                          <td style={{ padding: '8px 6px', border: '1px solid #cbd5e1', color: '#1e293b' }}>
                            {item.freeHrs || 0}
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                ))}

                {/* Totals Summary Row */}
                <tr style={{ background: '#f8fafc', fontWeight: '700', borderTop: '2px solid #94a3b8' }}>
                  <td style={{ padding: '12px 14px', border: '1px solid #cbd5e1', textAlign: 'left', color: '#0f172a' }}>
                    TOTALS
                  </td>
                  <td style={{ padding: '10px 6px', border: '1px solid #cbd5e1', color: '#0f172a', fontWeight: '700' }}>
                    {totals.acres || 0}
                  </td>
                  <td style={{ padding: '10px 6px', border: '1px solid #cbd5e1', color: '#0f172a', fontWeight: '700' }}>
                    {totals.noOfMembers || 0}
                  </td>
                  {machines.map((m) => {
                    const item = totals[m.code] || {};
                    return (
                      <React.Fragment key={`totals-${m.code}`}>
                        <td style={{ padding: '10px 6px', border: '1px solid #cbd5e1', color: '#0f172a' }}>
                          {item.noOfMachines || 0}
                        </td>
                        <td style={{ padding: '10px 6px', border: '1px solid #cbd5e1', color: '#0f172a', background: '#fef3c7' }}>
                          {item.authNoOfMachines || 0}
                        </td>
                        <td style={{ padding: '10px 6px', border: '1px solid #cbd5e1', background: 'rgb(127, 127, 127)', color: '#ffffff' }}>
                          {item.prevAvgUtilization ? item.prevAvgUtilization.toFixed(2) : '0.00'}
                        </td>
                        <td style={{ padding: '10px 6px', border: '1px solid #cbd5e1', background: 'rgb(127, 127, 127)', color: '#ffffff' }}>
                          {item.avgUtilization ? item.avgUtilization.toFixed(2) : '0.00'}
                        </td>
                        <td style={{ padding: '10px 6px', border: '1px solid #cbd5e1', color: '#0f172a' }}>
                          {item.chargeableHrs || 0}
                        </td>
                        <td style={{ padding: '10px 6px', border: '1px solid #cbd5e1', color: '#0f172a' }}>
                          {item.freeHrs || 0}
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>

          {/* 13 Machine Utilization Band Distribution Tables */}
          <div style={{ marginTop: '30px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {machines.map((m) => (
            <div
              key={`band-table-${m.code}`}
              style={{
                border: '1px solid #c2b55b',
                borderRadius: '4px',
                overflow: 'hidden',
                background: '#ffffff',
                boxShadow: '0 2px 6px rgba(0,0,0,0.05)'
              }}
            >
              {/* Gold Header Bar */}
              <div
                style={{
                  background: '#c2b55b',
                  color: '#ffffff',
                  padding: '12px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  justify: 'space-between',
                  fontWeight: '700',
                  fontSize: '16px',
                  letterSpacing: '0.5px'
                }}
              >
                <div style={{ textTransform: 'uppercase' }}>{m.name}</div>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const drEmail = user?.email || user?.EmailID || '';
                      const queryParams = new URLSearchParams({
                        drEmail,
                        periodId: selectedPeriodId
                      });
                      if (selectedDistrict) queryParams.append('districtName', selectedDistrict);
                      if (selectedArCircle) queryParams.append('arName', selectedArCircle);

                      const res = await fetch(`${API_BASE_URL}/api/reports/society-machine-summary?${queryParams.toString()}`);
                      const data = await res.json();

                      if (data.success && Array.isArray(data.rows)) {
                        const headers = ["Division", "District", "AR Circle", "Society", `${m.name} UTILIZATION`];
                        const csvRows = [headers.join(',')];

                        data.rows.forEach(r => {
                          csvRows.push([
                            `"${r.DIVISION || ''}"`,
                            `"${r.DISTRICT || ''}"`,
                            `"${r.AR_CIRCLE || ''}"`,
                            `"${r.SOCIETY || ''}"`,
                            `"${r[`${m.code}_UTILIZATION`] || '0 Utilization'}"`
                          ].join(','));
                        });

                        const csvContent = "data:text/csv;charset=utf-8," + csvRows.join('\n');
                        const encodedUri = encodeURI(csvContent);
                        const link = document.createElement("a");
                        link.setAttribute("href", encodedUri);
                        link.setAttribute("download", `${m.name.replace(/\s+/g, '_')}_Society_Utilization_${new Date().toISOString().split('T')[0]}.csv`);
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }
                    } catch (err) {
                      console.error('Error exporting machine utilization band CSV:', err);
                    }
                  }}
                  style={{
                    background: '#facc15',
                    color: '#1e293b',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '6px 14px',
                    fontWeight: '600',
                    fontSize: '12px',
                    cursor: 'pointer',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                >
                  Export Data
                </button>
              </div>

              {/* Band Table */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'center' }}>
                  <thead>
                    <tr style={{ background: '#ffffff', borderBottom: '1px solid #cbd5e1' }}>
                      <th
                        colSpan={5}
                        style={{
                          padding: '10px',
                          color: '#334155',
                          fontWeight: '700',
                          fontSize: '13px',
                          borderBottom: '1px solid #cbd5e1'
                        }}
                      >
                        No of Societies in different Average Utilization Bands
                      </th>
                    </tr>
                    <tr style={{ background: '#f8fafc', color: '#1e293b', borderBottom: '1px solid #cbd5e1' }}>
                      <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '700', borderRight: '1px solid #cbd5e1', width: '22%' }}>
                        {selectedDistrict ? 'AR CIRCLE' : 'DISTRICT'}
                      </th>
                      <th style={{ padding: '10px 14px', fontWeight: '700', borderRight: '1px solid #cbd5e1', width: '19.5%' }}>
                        0 UTILIZATION
                      </th>
                      <th style={{ padding: '10px 14px', fontWeight: '700', borderRight: '1px solid #cbd5e1', width: '19.5%' }}>
                        UPTO 30 HRS
                      </th>
                      <th style={{ padding: '10px 14px', fontWeight: '700', borderRight: '1px solid #cbd5e1', width: '19.5%' }}>
                        30-50 HRS
                      </th>
                      <th style={{ padding: '10px 14px', fontWeight: '700', width: '19.5%' }}>
                        ABOVE 50 HRS
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, rIdx) => {
                      const item = r[m.code] || {};
                      return (
                        <tr
                          key={`${m.code}-${r.name}`}
                          style={{
                            background: rIdx % 2 === 0 ? '#ffffff' : '#f8fafc',
                            borderBottom: '1px solid #cbd5e1'
                          }}
                        >
                          <td
                            style={{
                              padding: '10px 14px',
                              textAlign: 'left',
                              borderRight: '1px solid #cbd5e1',
                              fontWeight: '700',
                              color: '#2563eb',
                              cursor: 'pointer',
                              textDecoration: 'underline'
                            }}
                            onClick={() => {
                              if (!selectedDistrict) {
                                handleDistrictClick(r.name);
                              } else {
                                handleArClick(r.name);
                              }
                            }}
                          >
                            {r.name}
                          </td>
                          <td style={{ padding: '10px 14px', borderRight: '1px solid #cbd5e1', color: '#1e293b' }}>
                            {item.zeroUtilCount || 0}
                          </td>
                          <td style={{ padding: '10px 14px', borderRight: '1px solid #cbd5e1', color: '#1e293b' }}>
                            {item.upto30Count || 0}
                          </td>
                          <td style={{ padding: '10px 14px', borderRight: '1px solid #cbd5e1', color: '#1e293b' }}>
                            {item.count30to50 || 0}
                          </td>
                          <td style={{ padding: '10px 14px', color: '#1e293b' }}>
                            {item.above50Count || 0}
                          </td>
                        </tr>
                      );
                    })}

                    {/* Totals Row */}
                    <tr style={{ background: '#f8fafc', fontWeight: '700', borderTop: '2px solid #cbd5e1' }}>
                      <td style={{ padding: '12px 14px', textAlign: 'left', borderRight: '1px solid #cbd5e1', color: '#0f172a' }}>
                        TOTALS
                      </td>
                      {(() => {
                        const totItem = totals[m.code] || {};
                        return (
                          <>
                            <td style={{ padding: '10px 14px', borderRight: '1px solid #cbd5e1', color: '#0f172a' }}>
                              {totItem.zeroUtilCount || 0}
                            </td>
                            <td style={{ padding: '10px 14px', borderRight: '1px solid #cbd5e1', color: '#0f172a' }}>
                              {totItem.upto30Count || 0}
                            </td>
                            <td style={{ padding: '10px 14px', borderRight: '1px solid #cbd5e1', color: '#0f172a' }}>
                              {totItem.count30to50 || 0}
                            </td>
                            <td style={{ padding: '10px 14px', color: '#0f172a' }}>
                              {totItem.above50Count || 0}
                            </td>
                          </>
                        );
                      })()}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </>
      )}
    </ListContainer>
  );
};

export default CooperativeMachinePerformanceReportDR;
